

import React, { useState, useEffect, SyntheticEvent } from 'react';
import styled from 'styled-components';

import {
  InputRowWithTextInput,
  InputRowWithEmailInput,
  InputRowWithTextArea,
} from './InputRow';
import ActionRow from './ActionRow';
import type {
  EmailItinerary,
  PatchMessageConfigurationValuesRequest,
} from '../../types/messageConfiguration';
import type { StyledComponentWithChildren } from '../../types/styledComponent';
import { encodeToBase64 } from '../../lib/encodeToBase64';

const FormCard: StyledComponentWithChildren = styled.div`
  display: flex;
  flex-direction: column;
`;

interface EmailItineraryProps {
  emailItinerary?: EmailItinerary;
  domainList: string[];
  emailList: string[];
  onSave: (patchRequest: PatchMessageConfigurationValuesRequest) => void;
}

const EmailItineraryForm: React.FC<EmailItineraryProps> = ({
  emailItinerary,
  domainList,
  emailList,
  onSave,
}) => {
  const [emailInput, updateEmailInput] = useState(emailItinerary?.fromEmail || '');
  const [aliasInput, updateAliasInput] = useState(emailItinerary?.fromAlias || '');
  const [headerInput, updateHeaderInput] = useState(emailItinerary?.header || '');
  const [openingInput, updateOpeningInput] = useState(emailItinerary?.opening || '');
  const [actionInput, updateActionInput] = useState(emailItinerary?.action || '');
  const [actionTripAssistInput, updateActionTripAssistInput] = useState(
    emailItinerary?.actionTripAssist || '',
  );
  const [actionOnWaiverAlertsInput, updateActionOnWaiverAlertsInput] = useState(
    emailItinerary?.actionOnWaiverAlerts || '',
  );
  const [closingInput, updateClosingInput] = useState(emailItinerary?.closing || '');

  const [emailInputMessage, updateEmailInputMessage] = useState<any>();

  // Though this may seem redundant, it's to allow for non-email causes of form error to be added in the below useEffect
  const [hasFormError, updateHasFormError] = useState(false);

  useEffect(() => {
    // if emailInputMessage is falsy or type !== error, set HasFormError false, else true
    updateHasFormError(emailInputMessage?.type === 'error');
  }, [emailInputMessage]);

  const namePrefix = 'emailItinerary';

  const updateInputStateWithProps = (): void => {
    updateEmailInput(emailItinerary?.fromEmail || '');
    updateAliasInput(emailItinerary?.fromAlias || '');
    updateHeaderInput(emailItinerary?.header || '');
    updateOpeningInput(emailItinerary?.opening || '');
    updateActionInput(emailItinerary?.action || '');
    updateActionTripAssistInput(emailItinerary?.actionTripAssist || '');
    updateActionOnWaiverAlertsInput(emailItinerary?.actionOnWaiverAlerts || '');
    updateClosingInput(emailItinerary?.closing || '');
  };

  const handleOnCancel = (): void => {
    updateInputStateWithProps();
  };

  useEffect(() => {
    updateInputStateWithProps();
  }, [emailItinerary]);

  const updateMessageConfigValues = (): void => {
    const patchRequest: PatchMessageConfigurationValuesRequest = {
      messageConfigurationValues: {
        emailItinerary: {
          ...(emailInput && { fromEmail: emailInput }),
          ...(aliasInput && { fromAlias: aliasInput }),
          ...(headerInput && { header: encodeToBase64(headerInput) }),
          ...(openingInput && { opening: encodeToBase64(openingInput) }),
          ...(actionInput && { action: actionInput }),
          ...(actionTripAssistInput && {
            actionTripAssist: actionTripAssistInput,
          }),
          ...(actionOnWaiverAlertsInput && {
            actionOnWaiverAlerts: actionOnWaiverAlertsInput,
          }),
          ...(closingInput && {
            closing: encodeToBase64(closingInput),
          }),
        },
      },
    };
    onSave(patchRequest);
  };

  return (
    <FormCard>
      <ActionRow
        id={`${namePrefix}Top`}
        saveDisabled={hasFormError}
        onCancel={handleOnCancel}
        onSave={updateMessageConfigValues}
      />

      <InputRowWithEmailInput
        name={`${namePrefix}FromEmail`}
        labelText="From Email Address:"
        inputValue={emailInput}
        onChange={updateEmailInput}
        setDisplayMessage={updateEmailInputMessage}
        displayMessage={emailInputMessage}
        domainList={domainList}
        emailList={emailList}
      />

      <InputRowWithTextInput
        name={`${namePrefix}FromAlias`}
        labelText="From Alias:"
        inputValue={aliasInput}
        onChange={(e: SyntheticEvent<HTMLInputElement>): void => {
          updateAliasInput(e.currentTarget.value);
        }}
      />

      <InputRowWithTextArea
        name={`${namePrefix}Header`}
        labelText="Header:"
        inputValue={headerInput}
        onChange={(e: SyntheticEvent<HTMLTextAreaElement>): void => {
          updateHeaderInput(e.currentTarget.value);
        }}
      />

      <InputRowWithTextArea
        name={`${namePrefix}Opening`}
        labelText="Opening:"
        inputValue={openingInput}
        onChange={(e: SyntheticEvent<HTMLTextAreaElement>): void => {
          updateOpeningInput(e.currentTarget.value);
        }}
      />

      <InputRowWithTextArea
        name={`${namePrefix}Action`}
        labelText="Action:"
        inputValue={actionInput}
        onChange={(e: SyntheticEvent<HTMLTextAreaElement>): void => {
          updateActionInput(e.currentTarget.value);
        }}
      />

      <InputRowWithTextArea
        name={`${namePrefix}ActionWithTripAssist`}
        labelText="Action With Trip Assist:"
        inputValue={actionTripAssistInput}
        onChange={(e: SyntheticEvent<HTMLTextAreaElement>): void => {
          updateActionTripAssistInput(e.currentTarget.value);
        }}
      />

      <InputRowWithTextArea
        name={`${namePrefix}ActionOnWaiverAlerts`}
        labelText="Action on Waiver Alerts:"
        inputValue={actionOnWaiverAlertsInput}
        onChange={(e: SyntheticEvent<HTMLTextAreaElement>): void => {
          updateActionOnWaiverAlertsInput(e.currentTarget.value);
        }}
      />

      <InputRowWithTextArea
        name={`${namePrefix}Closing`}
        labelText="Closing:"
        inputValue={closingInput}
        onChange={(e: SyntheticEvent<HTMLTextAreaElement>): void => {
          updateClosingInput(e.currentTarget.value);
        }}
      />

      <ActionRow
        id={`${namePrefix}Bottom`}
        saveDisabled={hasFormError}
        onCancel={handleOnCancel}
        onSave={updateMessageConfigValues}
      />
    </FormCard>
  );
};

export default EmailItineraryForm;
